import {
  IsFullAccess,
  formatQueryRes,
  getInstanceDetail,
  useAppSelector,
  useGetInstanceInfoQuery,
  useRebootMutation
} from '@monorepo/shared';
import { selectAccountAllInstances } from '@monorepo/shared/store/haInstanceSlice';
import { selectCurrentAccount } from '@monorepo/shared/store/accountSlice';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ButtonToolbar, Loader, Stack, Tabs, useToaster } from 'rsuite';
import { AssetDetail } from './AssetList';
import {
  ErrorToaster,
  InstanceConfirmBtn,
  InstanceStatus
} from './InstanceStatus';
import { HomeElms, InstanceAction } from '../../config/const';
import { InstanceSnapshot } from './InstanceSnapshot';
import { t } from 'i18next';
import { InstanceResource } from './InstanceResource';
import { DateTime } from 'luxon';
import { GoDotFill } from 'react-icons/go';
import { InstanceTicket } from './InstanceTicket';

export const InstanceDetail = () => {
  const { instanceId, accountId, groupId } = useParams();
  const accountRegionInstances = useAppSelector(selectAccountAllInstances);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [instance, setInstance] = useState<Record<string, any>>({});
  const [instanceData, setInstanceData] = useState<Record<string, any>>({});
  const [instanceDetail, setInstanceDetail] = useState<Record<string, any>>({});
  const [region, setRegion] = useState<Record<string, any>>({});
  const [instanceReboot, rebootResult] = useRebootMutation();
  const toaster = useToaster();
  const {
    data: instanceInfo,
    isLoading: instanceInfoLoading,
    isFetching: instanceInfoFetching
  } = useGetInstanceInfoQuery(
    formatQueryRes({
      account: currentAccount,
      instance_id: instanceId,
      region
    }),
    { skip: !region.token }
  );

  useEffect(() => {
    const accountData = accountRegionInstances?.[accountId || ''];
    if (accountData && instanceId) {
      const inst = accountData.find(
        (obj: Record<string, any>) => obj.instance_id == instanceId
      );
      inst && setInstance({ ...inst });
      inst?.region && setRegion(inst.region);
    }
  }, [accountRegionInstances, instanceId]);
  useEffect(() => {
    if (instanceInfo) {
      const data = {
        ...instance,
        ...instanceInfo,
        cluster_info: {
          ...(instance?.cluster_info || {}),
          ...(instanceInfo?.cluster_info || {})
        },
        region_alias: region?.alias,
        instance_status_display: instance?.actionText
          ? instance.actionText
          : instance.is_active
          ? 'Active'
          : 'Inactive'
      };
      setInstanceData(data);
      const detail = getInstanceDetail(data);
      setInstanceDetail(detail);
    }
  }, [instanceInfo, instance]);

  useEffect(() => {
    if (rebootResult.isSuccess) {
      setTimeout(() => {
        setInstanceData({
          ...instanceData,
          action: InstanceAction.reboot,
          actionText: 'Reboot'
        });
      }, 2000);
    }
    if (rebootResult.isError) {
      ErrorToaster({
        toaster,
        error: rebootResult.error,
        title: 'Reboot Instance Failed!'
      });
    }
  }, [rebootResult]);

  return (
    <div className="instance-details-container">
      <div className="grid-container row">
        <div className="grid-item instance">
          <div className="grid-header">
            <div className="title">
              <i className="icon-fortinetone-tagsIcon"></i>
              Instance Information
            </div>
          </div>
          <div
            className="grid-content"
            style={{ justifyContent: 'flex-start' }}
          >
            {instanceInfoLoading || instanceInfoFetching ? (
              <div style={{ height: '100%', position: 'relative' }}>
                <Loader size="xs" content="Loading..." center />
              </div>
            ) : (
              <>
                <Tabs defaultActiveKey="general" appearance="subtle">
                  <Tabs.Tab eventKey="general" title="General">
                    {instanceGeneralInfo(instanceData)}
                  </Tabs.Tab>
                  {instanceData?.cluster_info?.node_role &&
                    instanceData?.cluster_info?.node_role !== 'Unknown' && (
                      <Tabs.Tab eventKey="cluster" title="Cluster Node Info">
                        {instanceClusterInfo(instanceData.cluster_info)}
                      </Tabs.Tab>
                    )}
                </Tabs>
              </>
            )}
          </div>
          {getAction(instanceData) === InstanceAction.reboot ? (
            <div style={{ display: 'grid', placeItems: 'center' }}>
              <InstanceStatus
                action={getAction(instanceData)}
                group_id={currentAccount?.group_id}
                account_id={accountId || ''}
                user_id={currentAccount?.user_id || ''}
                access_from_user_id={currentAccount?.access_from?.user_id || ''}
                instance_id={instanceId || ''}
                license_sn={instanceData.license_sn}
                region={instanceData.region}
              />
            </div>
          ) : (
            <ButtonToolbar style={{ justifyContent: 'center' }}>
              <>
                <InstanceConfirmBtn
                  btnText="Reboot"
                  title="Instance Reboot"
                  content={t('common.reboot.confirmation')}
                  disabled={
                    getAction(instanceData) !== 0 ||
                    rebootResult.isLoading ||
                    !IsFullAccess(currentAccount, HomeElms.fcName)
                  }
                  callBackFn={async () => {
                    instanceReboot({
                      account_id: accountId,
                      user_id: currentAccount.user_id,
                      access_from_user_id:
                        currentAccount.access_from_user_id ||
                        currentAccount.access_from?.user_id,
                      instance_id: instanceId,
                      region: region
                    });
                  }}
                />
              </>
            </ButtonToolbar>
          )}
        </div>
        <div className="grid-item asset">
          <div className="grid-header">
            <div className="title">
              <i className="icon-fortinetone-productViewIcon"></i>
              License Details
            </div>
          </div>
          <div className="grid-content">
            <AssetDetail
              account_id={accountId || ''}
              sn={instance.license_sn}
            />
          </div>
        </div>
      </div>
      <div className="grid-container ">
        <InstanceResource
          instanceData={instanceData}
          instanceDetail={instanceDetail}
        />
        {instanceData?.support_snapshot && (
          <InstanceSnapshot
            region={region}
            action={getAction(instanceData)}
            sn={instance.license_sn}
          />
        )}
        {/* {currentAccount?.user_type === 'Master' && !groupId && ( */}
        <InstanceTicket
          data={{
            name: currentAccount?.master_user_name,
            sn: instance.license_sn,
            instance_id: instance.instance_id,
            region: region,
            account_id: currentAccount?.account_id,
            user_id: currentAccount?.user_id,
            access_from_user_id:
              currentAccount.access_from_user_id ||
              currentAccount.access_from?.user_id,
            email: currentAccount?.account_email,
            firmware: instance.firmware
          }}
        />
        {/* )} */}
      </div>
    </div>
  );
};
export const instanceGeneralInfo = (instanceData: Record<string, any>) => {
  const instanceCols: Record<string, string> = {
    license_sn: 'License SN',
    instance_id: 'Instance ID',
    region_alias: 'Region',
    service_desc: 'Service description',
    expiry_date: 'Expiration Date',
    firmware: 'Service Version',
    instance_status_display: 'Status'
  };
  return (
    <div className="info-list">
      {Object.keys(instanceData)
        .filter((key) => Object.keys(instanceCols).includes(key))
        .map((key: string, index: number) => {
          return (
            <Stack
              key={index}
              alignItems="flex-start"
              style={{ marginBottom: '5px' }}
            >
              <Stack.Item className="list-type">{instanceCols[key]}</Stack.Item>
              <Stack.Item className="list-content" grow={1}>
                {instanceData[key]}
              </Stack.Item>
            </Stack>
          );
        })}
    </div>
  );
};
export const instanceClusterInfo = (clusterData: Record<string, any>) => {
  const clusterCols: Record<string, string> = {
    node_role: 'Node Role',
    node_status: 'Node Status',
    health_status_text: 'Health Status',
    timestamp: 'Collection time'
  };
  const getDateStr = (inDate: string | number) => {
    if (inDate) {
      const date = DateTime.fromMillis(Number(inDate) * 1000);
      const humanReadable = date.toLocaleString(DateTime.DATETIME_MED);
      return humanReadable;
    }
    return '';
  };
  return clusterData ? (
    <div className="info-list">
      {Object.keys(clusterData)
        .filter((key) => Object.keys(clusterCols).includes(key))
        .map((key: string, index: number) => {
          return (
            <Stack
              key={index}
              alignItems="flex-start"
              style={{ marginBottom: '5px' }}
            >
              <Stack.Item className="list-type">{clusterCols[key]}</Stack.Item>
              {key === 'timestamp' ? (
                <>{getDateStr(clusterData[key])}</>
              ) : (
                <Stack.Item className="list-content" grow={1}>
                  {clusterData[key]}
                </Stack.Item>
              )}
            </Stack>
          );
        })}
    </div>
  ) : (
    <></>
  );
};
export const getAction = (obj: Record<string, any>): number | string => {
  // check get state update
  // console.log(
  //   'get action input',
  //   obj.action,
  //   obj.instance_get_state,
  //   obj.snapshot_status,
  //   obj.revert_status
  // );
  let action = obj?.action || 0;
  if (action) {
    // console.log('get action return', obj.instance_get_state);
    return ['ACTIVE', 'SUCCESS'].includes(obj?.instance_get_state) ? 0 : action;
  }
  // check get_instance_list refetch update
  if (
    obj?.snapshot_status &&
    !['SUCCESS', 'ERROR'].includes(obj?.snapshot_status)
  ) {
    // console.log(
    //   'get action got 4',
    //   obj.instance_get_state,
    //   obj.snapshot_status
    // );
    action = InstanceAction.createSnapshot;
  } else if (
    obj?.revert_status &&
    !['SUCCESS', 'ERROR'].includes(obj?.revert_status)
  ) {
    // console.log('get action got 5', obj.instance_get_state, obj.revert_status);
    action = InstanceAction.revertSnapshot;
  }
  // server side removed instance.instance_status so no need to check action from instance_status
  // else if (obj?.instance_status === 'REBOOT') {
  //   action = InstanceAction.reboot;
  // }
  return action;
};
